import { useState, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign } from 'lucide-react';
import {
  useCompanyExpenses,
  type ExpenseInput,
} from '@/hooks/useCompanyExpenses';
import { ExpenseSummaryCards } from './expenses/ExpenseSummaryCards';
import { ExpenseTile } from './expenses/ExpenseTile';
import { ExpenseGroupSheet } from './expenses/ExpenseGroupSheet';
import { ExpenseMonthFilter } from './expenses/ExpenseMonthFilter';
import { EXPENSE_GROUPS, type ExpenseGroup } from './expenses/expenseGroups';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { generateMonthOptions, parseMonthValue } from '@/utils/monthOptions';

export const ExpensesReport = () => {
  const { timezone } = useTimezone();
  const { locale } = useCompanyLocale();
  const {
    activeExpenses,
    isLoading,
    addExpense,
    updateExpense,
    deleteExpense,
    calculateMonthlyAmount,
    calculatePeriodAmount,
    filterExpensesByPeriod,
  } = useCompanyExpenses();

  // Get current month as default
  const monthOptions = useMemo(() => generateMonthOptions(timezone), [timezone]);
  const currentMonth = monthOptions.length > 0 ? monthOptions[0].value : '';

  // Month filter state - default to current month
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);

  // Sheet state
  const [selectedGroup, setSelectedGroup] = useState<ExpenseGroup | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Filter expenses based on selected month
  const filteredExpenses = useMemo(() => {
    if (!selectedMonth) return activeExpenses;

    const { startDate, endDate } = parseMonthValue(selectedMonth, timezone);
    return filterExpensesByPeriod(activeExpenses, startDate, endDate);
  }, [activeExpenses, selectedMonth, timezone, filterExpensesByPeriod]);

  // Calculate totals for filtered expenses (includes one-time at full value)
  const monthlyTotal = useMemo(() => {
    return filteredExpenses.reduce(
      (sum, expense) => sum + calculatePeriodAmount(expense),
      0
    );
  }, [filteredExpenses, calculatePeriodAmount]);

  // Separate one-time totals
  const oneTimeTotal = useMemo(() => {
    return filteredExpenses
      .filter(e => e.frequency === 'one_time')
      .reduce((sum, e) => sum + e.amount, 0);
  }, [filteredExpenses]);

  // Get period label for display
  const periodLabel = useMemo(() => {
    if (!selectedMonth) return null;
    const [year, month] = selectedMonth.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString(locale.locale, { month: 'long', year: 'numeric' });
  }, [selectedMonth, locale.locale]);

  // Days in selected month for daily average
  const daysInMonth = useMemo(() => {
    if (!selectedMonth) return 30;
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  }, [selectedMonth]);

  const handleAdd = (data: ExpenseInput) => {
    // Auto-set start_date to first of selected month if not provided
    const expenseData = {
      ...data,
      start_date: data.start_date || (selectedMonth ? `${selectedMonth}-01` : undefined),
    };
    addExpense.mutate(expenseData);
  };

  const handleUpdate = (id: string, data: ExpenseInput) => {
    updateExpense.mutate({ id, ...data });
  };

  const handleDelete = (id: string) => {
    deleteExpense.mutate(id);
  };

  const handleTileClick = (group: ExpenseGroup) => {
    setSelectedGroup(group);
    setIsSheetOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-36" />
          ))}
        </div>
      </div>
    );
  }

  // Group expenses by their category groups
  const groupedExpenses = EXPENSE_GROUPS.map(group => {
    const expenses = filteredExpenses.filter(e =>
      group.categories.includes(e.category)
    );
    const groupMonthlyTotal = expenses.reduce(
      (sum, e) => sum + calculatePeriodAmount(e),
      0
    );
    return { group, expenses, monthlyTotal: groupMonthlyTotal };
  });

  // Get expenses for the selected group
  const selectedGroupExpenses = selectedGroup
    ? filteredExpenses.filter(e => selectedGroup.categories.includes(e.category))
    : [];
  const selectedGroupTotal = selectedGroupExpenses.reduce(
    (sum, e) => sum + calculatePeriodAmount(e),
    0
  );

  const hasAnyExpenses = filteredExpenses.length > 0;

  return (
    <div className="space-y-6">
      {/* Month Filter */}
      <ExpenseMonthFilter
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
      />

      {/* Summary Cards */}
      <ExpenseSummaryCards
        monthlyTotal={monthlyTotal}
        oneTimeTotal={oneTimeTotal}
        periodLabel={periodLabel}
        daysInMonth={daysInMonth}
      />

      {/* Period context message */}
      <p className="text-sm text-muted-foreground">
        {hasAnyExpenses
          ? `Showing ${filteredExpenses.length} expense${filteredExpenses.length !== 1 ? 's' : ''} for ${periodLabel}`
          : `No expenses yet for ${periodLabel}`
        }
      </p>

      {/* Expense Tiles Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {groupedExpenses.map(({ group, expenses, monthlyTotal: groupTotal }) => (
          <ExpenseTile
            key={group.key}
            group={group}
            expenses={expenses}
            monthlyTotal={groupTotal}
            onClick={() => handleTileClick(group)}
          />
        ))}
      </div>

      {/* Empty State hint */}
      {!hasAnyExpenses && (
        <div className="text-center py-6 border rounded-lg bg-muted/30">
          <DollarSign className="h-10 w-10 mx-auto mb-2 text-muted-foreground/40" />
          <p className="text-muted-foreground text-sm">
            Click any tile above to add expenses
          </p>
        </div>
      )}

      {/* Expense Group Sheet */}
      <ExpenseGroupSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        group={selectedGroup}
        expenses={selectedGroupExpenses}
        monthlyTotal={selectedGroupTotal}
        calculateAmount={calculatePeriodAmount}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        isAdding={addExpense.isPending}
        isUpdating={updateExpense.isPending}
        selectedMonth={selectedMonth}
        periodLabel={periodLabel}
      />
    </div>
  );
};
